import { useMutation, useQueryClient } from "@tanstack/react-query"
import { ImageOff, Moon, Sun, Trash2, Upload } from "lucide-react"
import { type ChangeEvent, useRef } from "react"

import { ErrorApi, pedir } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"

import { type Empresa, type ModoLogo, urlLogo } from "./tipos"

/**
 * Uno de los dos huecos de logo.
 *
 * ⚠ **El hueco oscuro NO está vacío cuando no hay logo oscuro: enseña el claro en
 * tinta blanca**, con una insignia que dice «Automático». Es lo que hacen los dos
 * sistemas de referencia, y evita la pregunta «¿tengo que subir dos?» — la respuesta
 * es no, y la pantalla lo demuestra en vez de explicarlo.
 *
 * ⚠ **SVG no se ofrece, aunque el 2.0 lo acepte.** Un SVG lleva scripts dentro y
 * servirlo desde nuestro origen es un XSS; la lista blanca del servidor lo rechaza
 * igual (regla 7), y el `accept` sólo evita el viaje.
 */

const ACEPTA = ".png,.jpg,.jpeg,.webp"

export function SubidaLogo({
  modo,
  empresa,
  puedeEditar,
}: {
  modo: ModoLogo
  empresa: Empresa
  puedeEditar: boolean
}) {
  const entrada = useRef<HTMLInputElement>(null)
  const clienteQuery = useQueryClient()

  const propio = modo === "claro" ? empresa.tiene_logo : empresa.tiene_logo_oscuro
  // El oscuro se deriva del claro mientras nadie suba uno suyo.
  const derivado = modo === "oscuro" && !empresa.tiene_logo_oscuro && empresa.tiene_logo
  const hay = propio || derivado
  const version = Number(empresa.tiene_logo) * 2 + Number(empresa.tiene_logo_oscuro)

  const refrescar = () => clienteQuery.invalidateQueries({ queryKey: ["empresa"] })

  const subir = useMutation({
    mutationFn: (archivo: File) => {
      const cuerpo = new FormData()
      cuerpo.append("archivo", archivo)
      return pedir<Empresa>(`/api/v1/empresa/logo/${modo}`, { method: "PUT", body: cuerpo })
    },
    onSuccess: () => {
      refrescar()
      if (entrada.current) entrada.current.value = ""
    },
  })

  const quitar = useMutation({
    mutationFn: () => pedir<Empresa>(`/api/v1/empresa/logo/${modo}`, { method: "DELETE" }),
    onSuccess: refrescar,
  })

  function alElegir(evento: ChangeEvent<HTMLInputElement>) {
    const archivo = evento.target.files?.[0]
    if (archivo) subir.mutate(archivo)
  }

  return (
    <div className="min-w-0 flex-1">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {modo === "claro" ? (
          <Sun size={13} aria-hidden="true" style={{ color: "var(--warning)" }} />
        ) : (
          <Moon size={13} aria-hidden="true" style={{ color: "var(--info)" }} />
        )}
        <span className="text-xs font-medium">
          {modo === "claro" ? "Logo para fondo claro" : "Logo para fondo oscuro"}
        </span>
        {derivado && <Insignia tono="info">Automático</Insignia>}
      </div>

      <div
        className="flex h-28 items-center justify-center rounded-[var(--radius-tarjeta)] border p-3"
        style={{
          borderColor: "var(--border)",
          // El fondo del hueco imita dónde va a ir el logo, no el tema actual: es lo
          // que permite juzgar si se lee.
          backgroundColor: modo === "claro" ? "#ffffff" : "#111111",
        }}
      >
        {hay ? (
          <img
            src={urlLogo(propio ? modo : "claro", version)}
            alt={modo === "claro" ? "Logo para fondo claro" : "Logo para fondo oscuro"}
            className={`max-h-full max-w-full object-contain ${derivado ? "tinta-blanca" : ""}`}
          />
        ) : (
          <span
            className="flex flex-col items-center gap-1 text-xs"
            style={{ color: modo === "claro" ? "#9a9aa2" : "#6b6b73" }}
          >
            <ImageOff size={20} aria-hidden="true" />
            Sin logo
          </span>
        )}
      </div>

      {puedeEditar && (
        <div className="mt-2 flex flex-wrap gap-2">
          <label
            className="flex h-8 cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border px-3 text-xs"
            style={{ borderColor: "var(--border)" }}
          >
            <Upload size={13} aria-hidden="true" />
            {subir.isPending ? "Subiendo…" : propio ? "Cambiar" : "Subir"}
            <input
              ref={entrada}
              type="file"
              className="sr-only"
              accept={ACEPTA}
              onChange={alElegir}
              disabled={subir.isPending}
            />
          </label>
          {propio && (
            <Boton
              variante="fantasma"
              tamano="sm"
              onClick={() => quitar.mutate()}
              disabled={quitar.isPending}
              style={{ color: "var(--destructive)" }}
            >
              <Trash2 size={13} aria-hidden="true" />
              Quitar
            </Boton>
          )}
        </div>
      )}

      {subir.isError && (
        <p role="alert" className="mt-2 text-xs" style={{ color: "var(--destructive)" }}>
          {subir.error instanceof ErrorApi ? subir.error.message : "No se pudo subir el logo"}
        </p>
      )}

      {modo === "oscuro" && (
        <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
          Opcional. Sin él se usa el claro en tinta blanca, y al imprimir siempre va el claro.
        </p>
      )}
    </div>
  )
}
